/**
 * Billing Service
 * Handles subscription, billing history, credits, and plans retrieval.
 * Integrates with backend /api/billing endpoints.
 */

import { BillingSettings, Invoice, SubscriptionPlan, SubscriptionStatus } from '../types/billing.types';
import { apiClient } from '../lib/api';

// Static Plans (Frontend definition matching backend/Stripe)
const PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    description: 'Ideal for individual clinicians starting with AI transcription',
    priceMonthly: 0,
    priceYearly: 0,
    currency: 'CAD',
    features: [
      'Unlimited AI dictation — free for now!',
      'Standard templates',
      'Ask charti, 10 actions /month',
     // 'Can be re-activated daily with one click',
     // 'Need more time? Use Charti for your full clinic day — only $2',
    ],
    isPopular: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Perfect for growing practices',
    priceMonthly: 59,
    priceYearly: 649, // 59 * 11
    currency: 'CAD',
    features: [
      'AI transcription for unlimited dictation',
      'Advanced templates',
      'Ask charti, unlimited actions',
      'Priority email & chat support',
      'Usage analytics & insights',
    ],
    isPopular: true,
  },
  {
    id: 'pair',
    name: 'Pair & Save',
    description: 'Two clinicians, one smart deal.',
    priceMonthly: 49,
    priceYearly: 539, // 49 * 11
    currency: 'CAD',
    features: [
      'All Pro plan benefits',
      'Lower annual pricing for two',
      'Separate billing per user',
      'Seamless transition if one leaves',
    ],
    isPopular: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Perfect for growing practices needing advanced features',
    priceMonthly: 0, // Contact Sales
    priceYearly: 0,
    currency: 'CAD',
    features: [
      'All pro plan benefits',
      'Custom AI and EMR integration',
      'API access for advanced workflows',
      'Volume pricing / usage tiers',
    ],
    isPopular: false,
  },
];

export interface CheckoutResponse {
    client_secret: string;
}

export interface PairValidationResponse {
    status: string;
    message: string;
}

export interface CreditBalanceResponse {
    balance: number;
}

export const billingService = {
  getAvailablePlans: async (): Promise<SubscriptionPlan[]> => {
    return PLANS;
  },

  getBillingSettings: async (contextId?: string): Promise<BillingSettings> => {
      // Fetch subscription from backend
      try {
        const response = await apiClient.get<any>('/api/billing/subscription');
        const sub = response.data;
  
        // Determine status and plan from backend response
        // Default to free/inactive if no sub or if sub is not valid
        const planId = sub?.plan_id || 'free';
        const isPairPending = planId === 'pair_pending';
        const status = sub?.status || 'inactive';
        
        // Map to BillingSettings
        return {
            plan: isPairPending ? 'pair' : planId,
            status: isPairPending ? 'pending_partner' : ((status as SubscriptionStatus) || 'inactive'),
            billingCycle: 'monthly', // TODO: Determine from Price ID or data
            nextBillingDate: sub?.current_period_end ? new Date(sub.current_period_end) : undefined,
            paymentMethod: sub?.payment_method_details ? {
                type: 'card', // Only cards supported right now
                brand: sub.payment_method_details.brand,
                last4: sub.payment_method_details.last4,
                expiryMonth: sub.payment_method_details.exp_month,
                expiryYear: sub.payment_method_details.exp_year,
            } : undefined,
            usageLimits: { maxUsers: 1, maxStorage: 10 }, // Defaults
            currentUsage: { users: 1, storage: 0 },
        };
      } catch (error) {
        // If 401/403 or network error, return default free plan state
        // Or rethrow? Better to show default/empty state
        console.warn("Failed to fetch billing settings, defaulting to free", error);
        return {
            plan: 'free',
            status: (status as SubscriptionStatus) || 'inactive',
            billingCycle: 'monthly',
            usageLimits: { maxUsers: 1, maxStorage: 10 },
            currentUsage: { users: 1, storage: 0 },
        };
      }
  },
  
  getSubscription: async (): Promise<BillingSettings> => {
      return billingService.getBillingSettings();
  },

  getInvoices: async (): Promise<Invoice[]> => {
    try {
        const response = await apiClient.get<Invoice[]>('/api/billing/invoices');
        return response.data || [];
    } catch (error) {
        console.error("Failed to fetch invoices", error);
        return [];
    }
  },

  getCreditsBalance: async (): Promise<number> => {
    try {
        const response = await apiClient.get<CreditBalanceResponse>('/api/billing/credits/balance');
        return response.data.balance || 0;
    } catch (error) {
        console.error("Failed to fetch credit balance", error);
        return 0;
    }
  },
  
  purchaseCredits: async (packId: string): Promise<string> => {
      const baseUrl = (window as any).env?.VITE_APP_URL || import.meta.env.VITE_APP_URL;
      if (!baseUrl) {
          console.warn("VITE_APP_URL is not set in environment variables");
      }
      // Returns client_secret
      const response = await apiClient.post<CheckoutResponse>('/api/billing/credits/purchase', {
          pack_id: packId,
          success_url: `${baseUrl}/settings/billing?success=true`,
          cancel_url: `${baseUrl}/settings/billing?canceled=true`,
      });
      return response.data.client_secret;
  },

  upgradePlan: async (planId: string, billingCycle: 'monthly' | 'yearly'): Promise<void> => {
    // This is handled by createPaymentIntent/Checkout flow usually
    // Trigger checkout logic from UI
  },

  updatePaymentMethod: async (paymentMethodId: string): Promise<void> => {
    // Placeholder
  },

  createPortalSession: async (returnUrl: string): Promise<string> => {
      const response = await apiClient.post<{url: string}>('/api/billing/portal', {
          return_url: returnUrl
      });
      return response.data.url;
  },

  createPaymentIntent: async (planId: string, billingCycle: 'monthly' | 'yearly', partnerEmail?: string): Promise<string> => {
      const baseUrl = (window as any).env?.VITE_APP_URL || import.meta.env.VITE_APP_URL || window.location.origin;
      if (!(window as any).env?.VITE_APP_URL && !import.meta.env.VITE_APP_URL) {
          console.warn("VITE_APP_URL is not set in environment variables, using window.location.origin instead");
      }
      const response = await apiClient.post<CheckoutResponse>('/api/billing/checkout', {
          plan_id: planId,
          billing_cycle: billingCycle,
          partner_email: partnerEmail,
          success_url: `${baseUrl}/settings/billing?success=true`,
          cancel_url: `${baseUrl}/settings/billing?canceled=true`,
      });
      return response.data.client_secret;
  },
  
  validatePairPartner: async (partnerEmail: string): Promise<PairValidationResponse> => {
      const response = await apiClient.post<PairValidationResponse>('/api/billing/pair/validate', {
          partner_email: partnerEmail
      });
      return response.data;
  }
};
